// Snippets live in their own git repository, configured under Settings > Snippets.
//
// INFO: A browser cannot clone a git repository — organice is a pure client-side
// app with no git implementation and no way to speak the git protocol. The
// repository is therefore *read over the GitLab API* instead, reusing the host and
// OAuth token the GitLab sync backend already holds (its scope is instance-wide
// 'api', so a second project needs no extra sign-in). That is the whole reason
// snippets require the GitLab backend — see snippetsAreAvailable().

import { gitLabProjectIdFromURL } from '../sync_backend_clients/gitlab_sync_backend_client';

export const DAYSTART_FAST = 'fast';
export const DAYSTART_SLOW = 'slow';

const SNIPPET_EXTENSION = /\.org$/i;

/**
 * 'https://www.fireweaver.org/org/snippets.git' -> 'org%2Fsnippets'
 *
 * The API addresses a project by its URL-encoded path, so the clone suffix and any
 * trailing slash have to go before the URL is parsed.
 */
export const snippetsProjectId = (repositoryUrl) => {
  const trimmed = `${repositoryUrl || ''}`.trim();
  if (!trimmed) return undefined;

  return gitLabProjectIdFromURL(trimmed.replace(/\/+$/, '').replace(/\.git$/i, ''));
};

// Snippets are usable only when a repository is configured *and* the sync backend
// is GitLab, because that is where the API token comes from.
export const snippetsAreAvailable = (syncBackendClient, repositoryUrl) =>
  !!syncBackendClient &&
  syncBackendClient.type === 'GitLab' &&
  typeof syncBackendClient.getSnippetListing === 'function' &&
  !!snippetsProjectId(repositoryUrl);

/**
 * Whether a repository file counts as a snippet.
 *
 * Accepted: '*.org', and files carrying no extension at all ('intro',
 * '10-morning-pages') — snippets are Org text whether or not they say so in the
 * filename. Rejected: any other extension, so a 'README.md' is not pasted into an
 * Org file as garbage headlines, and dotfiles like '.gitignore', which have no
 * business being inserted either.
 */
export const isSnippetFile = (name) => {
  const basename = `${name || ''}`.split('/').pop() || '';

  if (!basename || basename.startsWith('.')) return false;
  if (SNIPPET_EXTENSION.test(basename)) return true;

  return !basename.includes('.');
};

// Concatenated in filename order, so a numeric prefix ('10-tasks.org') fixes the
// order of a daystart without anyone having to touch the settings.
export const sortSnippetsByName = (snippets) =>
  [...(snippets || [])].sort((a, b) => `${a.name}`.localeCompare(`${b.name}`, 'en'));

/**
 * '/daystart/fast/10-morning-pages.org' -> 'morning pages'
 *
 * The leading '10-' exists only to order the file, so it is noise in a menu.
 *
 * At most three digits are treated as an ordering prefix: four or more are far more
 * likely to be part of the name ('2026-goals.org' should stay '2026 goals').
 */
export const snippetLabel = (path) => {
  const basename = `${path || ''}`.split('/').pop() || '';

  return basename
    .replace(SNIPPET_EXTENSION, '')
    .replace(/^\d{1,3}[-_. ]+/, '')
    .replace(/[-_]+/g, ' ')
    .trim();
};

/**
 * Join snippet bodies into one Org fragment: exactly one blank line between them
 * and a single trailing newline, so appending the result yields a well-formed file
 * instead of glued-together headlines.
 */
export const concatenateSnippets = (contents) => {
  const parts = (contents || [])
    .map((content) => `${content === null || content === undefined ? '' : content}`.replace(/\s+$/, ''))
    .filter((content) => content.length > 0);

  return parts.length ? `${parts.join('\n\n')}\n` : '';
};

// Which folder a daystart draws its snippets from. Kept here so the action drawer,
// the dashboard and the settings screen cannot disagree about it.
export const daystartFolder = (kind, { fastDaystartFolder, slowDaystartFolder }) =>
  kind === DAYSTART_SLOW ? slowDaystartFolder : fastDaystartFolder;

// ── Filtering ────────────────────────────────────────────────────────────────

// '*', '?' and '|' are the filter syntax; anything else in a pattern is literal.
const GLOB_CHARACTERS = /[*?|]/;

// Everything regex-special except '*' and '?', which carry meaning here.
const escapeForRegExp = (text) => `${text}`.replace(/[.+^${}()[\]\\/-]/g, '\\$&');

/**
 * A configured folder may end in a filter: '/daystart/fast/morning-*'.
 *
 * The last segment is treated as a filter only when it actually contains filter
 * syntax, so an ordinary folder path keeps working untouched.
 *
 * @returns {{folder: string, filter: string}} filter is '' when none was given.
 */
export const splitFolderFilter = (configured) => {
  const trimmed = `${configured || ''}`.trim();
  if (!trimmed) return { folder: '', filter: '' };

  const lastSlash = trimmed.lastIndexOf('/');
  const lastSegment = lastSlash === -1 ? trimmed : trimmed.slice(lastSlash + 1);

  if (!GLOB_CHARACTERS.test(lastSegment)) {
    return { folder: trimmed, filter: '' };
  }

  return {
    folder: lastSlash <= 0 ? trimmed.slice(0, lastSlash + 1) : trimmed.slice(0, lastSlash),
    filter: lastSegment,
  };
};

/**
 * Compile a filter into an anchored, case-insensitive regular expression.
 *
 * '*' matches any run of characters, '?' exactly one, and '|' separates whole
 * alternatives ('morning*|evening*'). Matching is case-insensitive because a
 * filter is typed by hand and a wrong capital is not worth a silently empty list.
 */
export const globToRegExp = (pattern) => {
  const alternatives = `${pattern || ''}`
    .split('|')
    .map((alternative) => alternative.trim())
    .filter((alternative) => alternative.length > 0);

  const source = (alternatives.length ? alternatives : ['*'])
    .map((alternative) => escapeForRegExp(alternative).replace(/\*/g, '.*').replace(/\?/g, '.'))
    .join('|');

  return new RegExp(`^(?:${source})$`, 'i');
};

/** An empty filter keeps everything, so an unfiltered folder needs no special case. */
export const matchesFilter = (name, pattern) => {
  if (!`${pattern || ''}`.trim()) return true;

  return globToRegExp(pattern).test(`${name || ''}`);
};

// ── Reading the repository ───────────────────────────────────────────────────

/** The snippets directly inside `folder`, in the order they will be inserted. */
export const listSnippets = async (syncBackendClient, projectId, folder, options) => {
  const listing = await syncBackendClient.getSnippetListing(projectId, folder, options);

  return sortSnippetsByName((listing || []).filter((file) => isSnippetFile(file.name)));
};

/** Every snippet in the repository, for the "which snippet?" menu. */
export const listAllSnippets = async (syncBackendClient, projectId) =>
  await listSnippets(syncBackendClient, projectId, '', { recursive: true });

/**
 * The snippets a daystart offers: the configured folder's contents, narrowed by the
 * filter that folder may carry ('/daystart/fast/morning-*').
 *
 * A daystart inserts one chosen snippet rather than the whole folder, so this is a
 * list to pick from, not something to concatenate.
 */
export const listDaystartSnippets = async (syncBackendClient, projectId, configuredFolder) => {
  const { folder, filter } = splitFolderFilter(configuredFolder);
  const snippets = await listSnippets(syncBackendClient, projectId, folder);

  return snippets.filter((snippet) => matchesFilter(snippet.name, filter));
};

/**
 * Whether `path` already exists in the *sync backend* (not the snippet repo).
 *
 * Every backend throws from getFileContents() for a missing file rather than
 * returning something falsy, so this is the one reliable existence check that does
 * not depend on paging through a directory listing.
 */
export const fileExists = async (syncBackendClient, path) => {
  try {
    await syncBackendClient.getFileContents(path);
    return true;
  } catch (error) {
    return false;
  }
};
